import { useState } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TablePagination
} from '@mui/material';
import {
    PersonAddOutlined,
    PaymentsOutlined,
    Visibility,
    MoreVertOutlined,
    EditOutlined,
    PersonOffOutlined,
    ReceiptLongOutlined
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, getRoleColor, getRoleLabel } from '../utils/dashboardUtils';

const getStatusChip = (type, value) => {
    if (!value)
        return (
            <Typography variant="body2" color="text.secondary">
                N/A
            </Typography>
        );

    const normalizedValue = value.toString().trim();
    const lowerValue = normalizedValue.toLowerCase();

    const statusMap = {
        bgv: {
            verified: 'success.main',
            'not verified': 'error.main'
        },
        training: {
            completed: 'success.main',
            'not completed': 'error.main',
            ongoing: 'info.main'
        }
    };

    const color = statusMap[type]?.[lowerValue] || 'text.secondary';

    return (
        <Typography variant="body2" fontWeight={600} color={color}>
            {normalizedValue}
        </Typography>
    );
};

const EmployeeActionMenu = ({
    emp,
    onOpenSalaryModal,
    onOpenDetailsModal,
    onOpenQuickView,
    onOpenDeactivationModal,
    onOpenSalaryListModal
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{ color: 'text.secondary', bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}
            >
                <MoreVertOutlined fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: { minWidth: 160, borderRadius: 2, mt: 0.5 }
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenDetailsModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <Visibility fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>View Details</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenQuickView(emp);
                    }}
                >
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Manage Portals</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSalaryModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <PaymentsOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Salary Payment</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSalaryListModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <ReceiptLongOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Salary List</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={emp.status !== 'Active'}
                    onClick={() => {
                        handleClose();
                        navigate('/HR/add-sales-employee', { state: { editEmployeeData: emp } });
                    }}
                >
                    <ListItemIcon>
                        <EditOutlined fontSize="small" color={emp.status !== 'Active' ? 'disabled' : 'action'} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Edit Employee</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenDeactivationModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <PersonOffOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500, color: 'error.main' }}>
                        Deactivate Employee
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

const EmployeeTable = ({
    employees,
    filteredEmployees,
    activeRole,
    setActiveRole,
    onOpenSalaryModal,
    onOpenDetailsModal,
    onOpenQuickView,
    onOpenDeactivationModal,
    onOpenSalaryListModal,
    onOpenStatusModal,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange
}) => (
    <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible">
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box
                sx={{
                    px: 3,
                    pt: 2.5,
                    pb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Employee Directory
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {activeRole
                            ? `Showing ${filteredEmployees.length} employees — ${getRoleLabel(activeRole)}`
                            : `Showing ${filteredEmployees.length} of ${totalCount} employees`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {activeRole && (
                        <Chip
                            label={`Role: ${getRoleLabel(activeRole)}`}
                            size="small"
                            color="primary"
                            onDelete={() => setActiveRole(null)}
                        />
                    )}
                    <Chip
                        icon={<PersonAddOutlined sx={{ fontSize: '14px !important' }} />}
                        label="Add Employee"
                        size="small"
                        color="primary"
                        variant="outlined"
                        component="a"
                        href="/HR/add-sales-employee"
                        clickable
                    />
                </Box>
            </Box>
            <Divider />
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            {['Employee', 'ID', 'Role', 'State', 'Status', 'BGV', 'Training', 'Joined', 'Actions'].map((h) => (
                                <TableCell
                                    key={h}
                                    sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', py: 1.5 }}
                                    align={h === 'Actions' ? 'center' : 'left'}
                                >
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <AnimatePresence>
                            {filteredEmployees.map((emp, i) => (
                                <motion.tr
                                    key={emp.id}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 12 }}
                                    transition={{ delay: i * 0.04, duration: 0.25 }}
                                    style={{ display: 'table-row' }}
                                >
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                                                    {emp.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="caption"
                                            sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}
                                        >
                                            {emp.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getRoleLabel(emp.role)}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setActiveRole((prev) => (prev === emp.role ? null : emp.role))}
                                            sx={{
                                                cursor: 'pointer',
                                                borderColor: `${getRoleColor(emp.role)}40`,
                                                color: getRoleColor(emp.role),
                                                bgcolor: `${getRoleColor(emp.role)}08`,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                '&:hover': {
                                                    bgcolor: `${getRoleColor(emp.role)}15`
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {emp.stateList || 'NA'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={emp.status === 'Active' ? 'Active' : 'Deactivated'}
                                            size="small"
                                            sx={{
                                                bgcolor: emp.status === 'Active' ? '#9bf2b9' : '#fee2e2',
                                                color: emp.status === 'Active' ? '#166534' : '#991b1b',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 20
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusChip('bgv', emp.bgv_status)}
                                            <IconButton
                                                size="small"
                                                onClick={() => onOpenStatusModal(emp, 'bgv')}
                                                sx={{
                                                    p: 0.5,
                                                    opacity: 0.3,
                                                    '&:hover': { opacity: 1, bgcolor: 'primary.light', color: 'primary.main' }
                                                }}
                                            >
                                                <EditOutlined sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusChip('training', emp.training_status)}
                                            <IconButton
                                                size="small"
                                                onClick={() => onOpenStatusModal(emp, 'training')}
                                                sx={{
                                                    p: 0.5,
                                                    opacity: 0.3,
                                                    '&:hover': { opacity: 1, bgcolor: 'primary.light', color: 'primary.main' }
                                                }}
                                            >
                                                <EditOutlined sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {emp.date_of_joining || 'NA'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <EmployeeActionMenu
                                            emp={emp}
                                            onOpenDetailsModal={onOpenDetailsModal}
                                            onOpenSalaryModal={onOpenSalaryModal}
                                            onOpenQuickView={onOpenQuickView}
                                            onOpenDeactivationModal={onOpenDeactivationModal}
                                            onOpenSalaryListModal={onOpenSalaryListModal}
                                        />
                                    </TableCell>
                                </motion.tr>
                            ))}
                        </AnimatePresence>
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
        </Paper>
    </motion.div>
);

EmployeeTable.propTypes = {
    employees: PropTypes.array.isRequired,
    filteredEmployees: PropTypes.array.isRequired,
    activeRole: PropTypes.string,
    totalCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    setActiveRole: PropTypes.func.isRequired,
    onOpenSalaryModal: PropTypes.func.isRequired,
    onOpenDetailsModal: PropTypes.func.isRequired,
    onOpenQuickView: PropTypes.func.isRequired,
    onOpenDeactivationModal: PropTypes.func.isRequired,
    onOpenSalaryListModal: PropTypes.func.isRequired,
    onOpenStatusModal: PropTypes.func.isRequired
};

export default EmployeeTable;
